import { callT } from '~/utils/callI18n';
import { notifyCall } from '~/utils/helper';
import { ICE_SERVERS, SUBSCRIBER_ICE_RECOVERY_MS, VIDEOROOM_PLUGIN } from './constants';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import type { SendJanusFn, SubscriberFeed, VideoRoomPublisher } from './types';
import { applyRemoteInboundAudio } from './remoteAudio';
import { drainPendingCandidates, logJanus, parsePublisherFeedRef, txId } from './utils';

export interface SubscriberDeps {
  sendJanus: SendJanusFn;
  sendToHandle: (
    handleId: number,
    body: Record<string, unknown>,
    jsep?: RTCSessionDescriptionInit,
    transaction?: string
  ) => void;
  isActiveCallSession: () => boolean;
}

export function subscribeToPublishers(
  r: JanusCallRefs,
  sendJanus: SendJanusFn,
  publishers?: VideoRoomPublisher[]
): void {
  if (!publishers?.length || !r.sessionIdRef.current) {
    logJanus('SUB', 'subscribe skipped — no publishers or session', {
      count: publishers?.length ?? 0,
      session: r.sessionIdRef.current,
    });
    return;
  }

  logJanus('SUB', 'subscribeToPublishers', publishers);

  for (const pub of publishers) {
    if (r.subscribedFeedIdsRef.current.has(pub.id)) continue;
    if (r.subscriberFeedsRef.current.some((s) => s.feed === pub.id)) continue;
    if (Array.from(r.pendingSubscriberAttachesRef.current.values()).includes(pub.id)) {
      continue;
    }
    if (r.myPublisherIdRef.current !== null && pub.id === r.myPublisherIdRef.current) continue;

    const transaction = `attach-sub-${pub.id}-${txId('')}`;
    r.pendingSubscriberAttachesRef.current.set(transaction, pub.id);

    logJanus('SUB', 'attach subscriber', { feed: pub.id, display: pub.display });
    sendJanus({
      janus: 'attach',
      session_id: r.sessionIdRef.current,
      plugin: VIDEOROOM_PLUGIN,
      transaction,
    });
  }
}

export function setupIncomingPeerConnection(
  r: JanusCallRefs,
  set: JanusCallSetters,
  sendJanus: SendJanusFn,
  senderHandleId: number,
  deps: Pick<SubscriberDeps, 'isActiveCallSession'>
): RTCPeerConnection {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

  pc.ontrack = (event) => {
    applyRemoteInboundAudio(event.track, event.streams?.[0], set, { handle: senderHandleId });
  };

  pc.onicecandidate = (event) => {
    if (!r.sessionIdRef.current) return;
    sendJanus({
      janus: 'trickle',
      session_id: r.sessionIdRef.current,
      handle_id: senderHandleId,
      candidate: event.candidate ? event.candidate : { completed: true },
    });
  };

  const scheduleReconnectIfNeeded = (reason: string) => {
    if (!deps.isActiveCallSession()) return;
    logJanus('RECONNECT', `subscriber PC ${reason} — schedule full reconnect`);
    r.wsReconnectAttemptsRef.current = 0;
    r.scheduleWebSocketReconnectRef.current(true);
  };

  let subscriberIceRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed') {
      scheduleReconnectIfNeeded(pc.connectionState);
    }
  };

  pc.oniceconnectionstatechange = () => {
    const iceState = pc.iceConnectionState;
    if (iceState === 'connected' || iceState === 'completed') {
      if (subscriberIceRecoveryTimer) clearTimeout(subscriberIceRecoveryTimer);
      subscriberIceRecoveryTimer = null;
      return;
    }
    if (iceState === 'failed') {
      scheduleReconnectIfNeeded(`ICE ${iceState}`);
      return;
    }
    if (iceState === 'disconnected') {
      if (subscriberIceRecoveryTimer) clearTimeout(subscriberIceRecoveryTimer);
      subscriberIceRecoveryTimer = setTimeout(() => {
        const s = pc.iceConnectionState;
        if (s === 'disconnected' || s === 'failed') {
          scheduleReconnectIfNeeded(`ICE ${s}`);
        }
      }, SUBSCRIBER_ICE_RECOVERY_MS);
    }
  };

  return pc;
}

export async function handleSubscriberOffer(
  r: JanusCallRefs,
  set: JanusCallSetters,
  data: Record<string, unknown>,
  sub: SubscriberFeed,
  deps: SubscriberDeps
): Promise<void> {
  const jsep = data.jsep as RTCSessionDescriptionInit | undefined;
  if (!jsep) return;

  if (jsep.type === 'offer') {
    try {
      logJanus('SUB', 'subscriber offer', { feed: sub.feed, handle: sub.handleId });
      const pc = setupIncomingPeerConnection(r, set, deps.sendJanus, sub.handleId, deps);
      sub.pc = pc;

      await pc.setRemoteDescription(new RTCSessionDescription(jsep));
      await drainPendingCandidates(pc, sub.pendingCandidates);

          const answer = await pc.createAnswer({ offerToReceiveAudio: true });
          await pc.setLocalDescription(answer);

      deps.sendToHandle(
        sub.handleId,
        { request: 'start', room: r.roomIdRef.current },
        answer,
        `start-sub-${sub.feed}`
      );

      sub.isMediaStarted = true;
      r.subscribedFeedIdsRef.current.add(sub.feed);
      logJanus('SUB', 'subscriber media started', { feed: sub.feed, handle: sub.handleId });
    } catch (err) {
      r.subscriberFeedsRef.current = r.subscriberFeedsRef.current.filter((s) => s.feed !== sub.feed);
      r.subscribedFeedIdsRef.current.delete(sub.feed);
      const detail = err instanceof Error ? err.message : '';
      const tr = r.translateCallRef.current;
      const label = callT('errors.subscriberSdp', tr);
      notifyCall('error', detail ? `${label}: ${detail}` : label, {
        toastId: 'call-subscriber-sdp',
      });
    }
    return;
  }

  if (jsep.type === 'answer' && sub.isMediaStarted && sub.pc) {
    try {
      await sub.pc.setRemoteDescription(new RTCSessionDescription(jsep));
      await drainPendingCandidates(sub.pc, sub.pendingCandidates);
    } catch {}
  }
}

export async function handleSubscriberEvent(
  r: JanusCallRefs,
  set: JanusCallSetters,
  data: Record<string, unknown>,
  sub: SubscriberFeed,
  pluginBody: Record<string, unknown>,
  deps: SubscriberDeps
): Promise<void> {
  const pluginError = pluginBody.error as string | undefined;

  if (pluginError) {
    const tr = r.translateCallRef.current;
    const label = callT('errors.subscriberPlugin', tr);
    notifyCall('error', pluginError ? `${label}: ${pluginError}` : label, {
      toastId: 'call-subscriber-plugin',
    });
    return;
  }

  const unpublishedFeedId = parsePublisherFeedRef(pluginBody.unpublished);
  if (unpublishedFeedId !== undefined && unpublishedFeedId === sub.feed) {
    logJanus('SUB', 'publisher unpublished', { feed: sub.feed });
    sub.pc?.close();
    r.subscriberFeedsRef.current = r.subscriberFeedsRef.current.filter((s) => s.feed !== sub.feed);
    r.subscribedFeedIdsRef.current.delete(sub.feed);
    set.setRemoteStream(null);
    if (r.subscriberFeedsRef.current.length === 0) {
      if (
        r.signalingResyncInFlightRef.current ||
        r.mediaReconnectPendingRef.current ||
        r.networkDropPendingRef.current
      ) {
        logJanus('SUB', 'unpublished during resync — wait for reconnect');
      } else {
        logJanus('SUB', 'no remote subscribers — remote call ended');
        r.onRemoteCallEndedRef.current?.();
      }
    }
    return;
  }

  await handleSubscriberOffer(r, set, data, sub, deps);
}
